import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Upload, Database, Loader2, CheckCircle2, AlertTriangle, Copy, FileSpreadsheet, Filter, Trash2, X, ShieldAlert } from "lucide-react";
import EnrichmentPanel from "@/components/EnrichmentPanel";
import ModuleHelp from "@/components/help/ModuleHelp";

const TABS = [
  { key: "valid", label: "Novas", color: "var(--success)", icon: CheckCircle2 },
  { key: "duplicates", label: "Duplicatas", color: "var(--warn)", icon: Copy },
  { key: "possible_duplicates", label: "Possíveis dup.", color: "var(--gold)", icon: AlertTriangle },
  { key: "invalid", label: "Inválidas", color: "var(--danger)", icon: AlertTriangle },
];

function StatusLinkBadge({ value }) {
  const map = {
    ATIVO: "badge-success",
    QUEBRADO: "badge-danger",
    DESCONHECIDO: "badge-warn",
  };
  return <span className={`badge ${map[value] || "badge-warn"} text-[10px]`}>{value || "—"}</span>;
}

export default function Legado() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [result, setResult] = useState(null); // {summary, valid, invalid, duplicates}
  const [activeTab, setActiveTab] = useState("valid");
  const [selected, setSelected] = useState({}); // row_index -> bool
  const [imported, setImported] = useState(null); // result of confirm
  const [search, setSearch] = useState("");
  const fileInputRef = useRef(null);
  const [statsTick, setStatsTick] = useState(0); // força reload dos contadores após enriquecimento

  // Stats da base já importada
  const [baseStats, setBaseStats] = useState({ total: 0, pendentes_enriquecimento: 0 });

  // Histórico de importações
  const [historyOpen, setHistoryOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get("/legacy-import/batches");
      setBatches(data.items || []);
    } catch (e) { setErr(formatErr(e)); }
  };

  // Cleanup mock data
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const openCleanup = async () => {
    setCleanupOpen(true);
    setCleanupPreview(null);
    setErr("");
    try {
      const { data } = await api.get("/legacy-import/cleanup-mock/preview");
      setCleanupPreview(data);
    } catch (e) { setErr(formatErr(e)); }
  };

  const confirmCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/legacy-import/cleanup-mock/confirm");
      setInfo(`Limpeza concluída — ${data.total_deleted} registros fictícios removidos (Fontes: ${data.sources_deleted}, Sementes: ${data.future_seeds_deleted}, Insumos: ${data.inputs_deleted}, Estatísticas: ${data.public_stats_deleted}).`);
      setCleanupOpen(false);
      setStatsTick(s => s + 1);
    } catch (e) { setErr(formatErr(e)); }
    finally { setCleanupBusy(false); }
  };

  useEffect(() => {
    api.get("/legacy-import/sources").then(({ data }) => {
      setBaseStats({ total: data.total || 0, pendentes_enriquecimento: data.pendentes_enriquecimento || 0 });
    }).catch(() => {});
  }, [imported, statsTick]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      setErr("Selecione um arquivo .xlsx do Excel.");
      return;
    }
    setFile(f);
    setResult(null);
    setImported(null);
    setErr("");
    setInfo("");
  };

  const simulate = async () => {
    if (!file) {
      setErr("Selecione um arquivo .xlsx primeiro.");
      return;
    }
    setBusy(true); setErr(""); setInfo(""); setResult(null); setImported(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/legacy-import/simulate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Validação defensiva — força arrays mesmo se backend retornar formato inesperado
      const safe = {
        summary: data?.summary || { total_lidas: 0, validas: 0, invalidas: 0, duplicadas: 0, possiveis_duplicidades: 0 },
        valid: Array.isArray(data?.valid) ? data.valid : [],
        invalid: Array.isArray(data?.invalid) ? data.invalid : [],
        duplicates: Array.isArray(data?.duplicates) ? data.duplicates : [],
        possible_duplicates: Array.isArray(data?.possible_duplicates) ? data.possible_duplicates : [],
      };
      setResult(safe);
      // Pré-seleciona todas as válidas
      const initial = {};
      safe.valid.forEach((r) => { if (r?.row_index != null) initial[r.row_index] = true; });
      setSelected(initial);
      setActiveTab("valid");
      setInfo(
        `Simulação concluída: ${safe.summary.validas} novas · ${safe.summary.duplicadas} duplicatas fortes · ` +
        `${safe.summary.possiveis_duplicidades || 0} possíveis dup. · ${safe.summary.invalidas} inválidas.`
      );
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (row_index) => {
    setSelected((s) => ({ ...s, [row_index]: !s[row_index] }));
  };

  const toggleAllInTab = (rows, checked) => {
    const next = { ...selected };
    rows.forEach((r) => { next[r.row_index] = checked; });
    setSelected(next);
  };

  // Chave estável por linha — usa stable_import_key (backend), legacy_id ou row_index
  const stableKey = (r) => {
    if (!r) return "row-empty";
    if (r.stable_import_key) return r.stable_import_key;
    const lid = r?.parsed?.legacy_id;
    if (lid != null && lid !== "") return `legacy-${lid}`;
    if (r.row_index != null) return `row-${r.row_index}`;
    // último recurso: chave determinística baseada no Date.now() seria instável a cada render;
    // como o backend SEMPRE injeta `stable_import_key`, esta linha só deve disparar com
    // dados malformados — usamos uma combinação do row_index + JSON.stringify hash do parsed.
    const fallback = `fallback-${r.row_index ?? "x"}-${String(r?.parsed?.titulo || "").slice(0, 20)}`;
    return fallback;
  };

  const visibleRows = useMemo(() => {
    if (!result) return [];
    const list = Array.isArray(result[activeTab]) ? result[activeTab] : [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((r) => {
      const p = (r && r.parsed) || {};
      return (
        (p.titulo || "").toLowerCase().includes(q) ||
        String(p.legacy_id || "").includes(q) ||
        (p.responsavel_legado || "").toLowerCase().includes(q) ||
        (p.tema_legado || "").toLowerCase().includes(q)
      );
    });
  }, [result, activeTab, search]);

  const selectedCount = useMemo(() => {
    if (!result) return 0;
    const valids = result.valid || [];
    return valids.filter((r) => selected[r.row_index]).length;
  }, [result, selected]);

  const confirmImport = async () => {
    if (!result) return;
    const rowsToImport = (result.valid || []).filter((r) => selected[r.row_index]);
    if (rowsToImport.length === 0) {
      setErr("Selecione ao menos uma linha válida para importar.");
      return;
    }
    if (!window.confirm(`Confirmar importação de ${rowsToImport.length} registro(s) para a Base Legada LSC?`)) return;
    setConfirming(true); setErr(""); setInfo("");
    try {
      const { data } = await api.post("/legacy-import/confirm", {
        rows: rowsToImport,
        filename: file?.name || null,
        ignored_count: result?.summary?.duplicadas || 0,
        possible_duplicates_count: result?.summary?.possiveis_duplicidades || 0,
        invalid_count: result?.summary?.invalidas || 0,
      });
      setImported(data);
      setInfo(`Importação concluída: ${data.inserted} inserido(s), ${data.skipped} ignorado(s).`);
      setResult(null);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setFile(null); setResult(null); setImported(null);
    setErr(""); setInfo(""); setSelected({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-4 min-w-0" data-testid="legado-page" translate="no">
      {/* Bloco operacional — Preparação da Base Oficial */}
      <div className="card-mil p-4 border-l-2 border-[var(--gold)]" data-testid="prep-base-oficial">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded bg-[var(--gold)]/15 border border-[var(--gold)] flex items-center justify-center text-[var(--gold)]">
            <ShieldAlert size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-mil text-[var(--gold)] mb-1">Preparação da Base Oficial</div>
            <p className="text-sm text-[var(--text-muted)] mb-3 leading-relaxed">
              Roteiro recomendado para carga oficial dos dados reais LSC/EGN. Cada etapa pode ser executada uma única vez por planilha.
            </p>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
              {[
                "1. Zerar dados fictícios (botão à direita).",
                "2. Carregar a planilha oficial em .xlsx.",
                "3. Simular para revisar contadores e duplicatas.",
                "4. Confirmar para inserir somente novos.",
                "5. Validar pesquisadores criados em /usuarios.",
                "6. Recalcular indicadores em /coleta-publica.",
              ].map((s) => (
                <li key={s} className="flex items-start gap-1.5 text-[var(--text)]">
                  <span className="text-[var(--gold)]">›</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mil text-[var(--gold)]">Importação · Base Legada LSC</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">
            <Database size={28} className="text-[var(--gold)]" />
            Importar Planilha Histórica
            <ModuleHelp module="legado" />
          </h1>
          <p className="text-[var(--text-muted)] text-sm mt-2 max-w-3xl">
            Carregue o arquivo <span className="font-mono">.xlsx</span> da base institucional LSC (Planilha Base com colunas:
            ID, Responsável, Ano, Autor(es), Título, Tipo, Tema, Idioma, Resumo, Link, DOI, Status, Obs).
            O sistema valida, detecta duplicatas (por ID legado, DOI ou link) e — após sua confirmação — registra cada item em
            <span className="badge badge-info ml-1">Base Legada LSC</span> com status
            <span className="badge badge-warn ml-1">Pendente de Enriquecimento</span> (Fase 2).
          </p>
          <div className="mt-3 max-w-3xl rounded-md border border-amber-200/60 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-800/40 px-3.5 py-2.5 text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
            Registros importados da planilha entram como <strong>fontes reais pendentes de enriquecimento</strong>.
            Eles não são automaticamente aprovados como Sementes de Futuro.
          </div>
        </div>
        <div className="flex gap-3 text-right flex-wrap items-stretch">
          <div className="card-mil px-4 py-3">
            <div className="label-mil text-[var(--text-muted)]">Já importadas</div>
            <div className="font-display text-2xl font-bold" data-testid="legado-stats-total">{baseStats.total}</div>
          </div>
          <div className="card-mil px-4 py-3">
            <div className="label-mil text-[var(--text-muted)]">Pendentes p/ IA</div>
            <div className="font-display text-2xl font-bold text-[var(--warn)]" data-testid="legado-stats-pendentes">{baseStats.pendentes_enriquecimento}</div>
          </div>
          <button
            onClick={openHistory}
            className="btn-ghost text-xs flex items-center gap-1.5 self-center"
            data-testid="btn-import-history"
          >
            <FileSpreadsheet size={12} /> Histórico
          </button>
          <button
            onClick={openCleanup}
            className="btn-ghost text-xs flex items-center gap-1.5 self-center text-[var(--danger)] border-[var(--danger)]/30 hover:bg-[var(--danger)]/10"
            data-testid="btn-cleanup-mock"
          >
            <Trash2 size={12} /> Zerar dados fictícios
          </button>
        </div>
      </div>

      {/* Modal de Histórico de Importações */}
      {historyOpen && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={() => setHistoryOpen(false)}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg max-w-5xl w-full max-h-[80vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="import-history-modal">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileSpreadsheet size={20} className="text-[var(--gold)]" />
                <h3 className="font-display font-bold text-lg">Histórico de Importações</h3>
              </div>
              <button onClick={() => setHistoryOpen(false)} className="text-[var(--text-muted)] hover:text-[var(--text)]">
                <X size={18} />
              </button>
            </div>

            {batches.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)] text-center py-8">
                Nenhuma importação registrada ainda.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--surface-2)]">
                    <tr>
                      <th className="px-3 py-2 label-mil text-left">Data</th>
                      <th className="px-3 py-2 label-mil text-left">Arquivo</th>
                      <th className="px-3 py-2 label-mil text-left">Por</th>
                      <th className="px-3 py-2 label-mil text-right">Lidas</th>
                      <th className="px-3 py-2 label-mil text-right">Inseridas</th>
                      <th className="px-3 py-2 label-mil text-right">Ignoradas</th>
                      <th className="px-3 py-2 label-mil text-right">Pesquisadores criados</th>
                      <th className="px-3 py-2 label-mil text-right">Vinculados</th>
                      <th className="px-3 py-2 label-mil text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.id} className="border-b border-[var(--border)]/50">
                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                          {b.uploaded_at ? new Date(b.uploaded_at).toLocaleString("pt-BR") : "—"}
                        </td>
                        <td className="px-3 py-2 truncate max-w-[180px]" title={b.file_name}>{b.file_name || "—"}</td>
                        <td className="px-3 py-2 text-xs">{b.uploaded_by_user_name || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{b.total_rows}</td>
                        <td className="px-3 py-2 text-right font-mono text-[var(--success)]">{b.inserted_count}</td>
                        <td className="px-3 py-2 text-right font-mono text-[var(--warn)]">{b.ignored_duplicates_count}</td>
                        <td className="px-3 py-2 text-right font-mono text-[var(--gold)]">{b.created_researchers_count}</td>
                        <td className="px-3 py-2 text-right font-mono">{b.linked_researchers_count}</td>
                        <td className="px-3 py-2">
                          <span className={`badge text-[10px] ${b.status === "completed" ? "badge-success" : "badge-muted"}`}>{b.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmação da limpeza */}
      {cleanupOpen && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={() => setCleanupOpen(false)}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cleanup-mock-modal">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldAlert size={20} className="text-[var(--danger)]" />
                <h3 className="font-display font-bold text-lg">Zerar dados fictícios</h3>
              </div>
              <button onClick={() => setCleanupOpen(false)} className="text-[var(--text-muted)] hover:text-[var(--text)]">
                <X size={18} />
              </button>
            </div>

            {!cleanupPreview ? (
              <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                <Loader2 size={14} className="animate-spin" /> Verificando dados...
              </div>
            ) : (
              <>
                <p className="text-sm text-[var(--text-muted)] mb-4">
                  Esta ação removerá <strong>apenas dados fictícios/mock</strong> e manterá usuários, configurações e dados reais.
                </p>

                <div className="space-y-1.5 mb-4 text-sm">
                  <Row label="Fontes mockadas" value={cleanupPreview.preview.sources} />
                  <Row label="Sementes mockadas" value={cleanupPreview.preview.future_seeds} />
                  <Row label="Insumos mockados" value={cleanupPreview.preview.inputs} />
                  <Row label="Estatísticas públicas mockadas" value={cleanupPreview.preview.public_observatory_stats} />
                  <Row label="Execuções públicas mockadas" value={cleanupPreview.preview.public_weekly_collection_runs} />
                  <div className="border-t border-[var(--border)] pt-2 mt-2 flex justify-between font-display font-bold">
                    <span>Total a remover</span>
                    <span className="text-[var(--danger)]">{cleanupPreview.total_to_delete}</span>
                  </div>
                </div>

                {cleanupPreview.total_to_delete === 0 && (
                  <div className="text-xs text-[var(--success)] bg-[var(--success)]/10 border border-[var(--success)]/30 rounded p-2 mb-4">
                    Nenhum dado fictício detectado. A base já está limpa de mocks marcados com flag explícita.
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <button onClick={() => setCleanupOpen(false)} className="btn-ghost text-xs" data-testid="btn-cleanup-cancel">
                    Cancelar
                  </button>
                  <button
                    onClick={confirmCleanup}
                    disabled={cleanupBusy || cleanupPreview.total_to_delete === 0}
                    className="text-xs px-4 py-2 rounded bg-[var(--danger)] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="btn-cleanup-confirm"
                  >
                    {cleanupBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Confirmar limpeza dos dados fictícios
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {err && <div className="text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded" data-testid="legado-err">{err}</div>}
      {info && <div className="text-sm text-[var(--success)] border border-[var(--success)]/40 bg-[var(--success)]/10 p-3 rounded" data-testid="legado-info">{info}</div>}

      {/* UPLOAD CARD */}
      {!result && (
        <div className="card-mil p-6 gold-accent-top">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={32} className="text-[var(--gold)]" />
                <div>
                  <div className="label-mil">Arquivo .xlsx</div>
                  <div className="font-mono text-sm mt-1" data-testid="legado-file-name">
                    {file ? file.name : "Nenhum arquivo selecionado"}
                  </div>
                  {file && (
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">{(file.size / 1024).toFixed(1)} KB</div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={onPickFile}
                data-testid="legado-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="btn-ghost flex items-center gap-2"
                data-testid="legado-choose-file"
              >
                <Upload size={14} /> Escolher arquivo
              </button>
              <button
                type="button"
                onClick={simulate}
                disabled={!file || busy}
                className="btn-gold flex items-center gap-2 disabled:opacity-50"
                data-testid="legado-simulate-btn"
              >
                {busy ? <><Loader2 size={14} className="animate-spin" /> Analisando…</> : <><Database size={14} /> Simular importação</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RESULT — Simulation table */}
      {result && (
        <>
          <div className="card-mil p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex flex-wrap gap-2">
                {TABS.map((t) => {
                  const count = (result[t.key] || []).length;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveTab(t.key)}
                      className={`px-3 py-1.5 text-xs font-mono rounded border flex items-center gap-2 ${activeTab === t.key ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)] text-[var(--text-muted)]"}`}
                      data-testid={`legado-tab-${t.key}`}
                    >
                      <Icon size={12} style={{ color: t.color }} />
                      {t.label}
                      <span className="badge badge-info text-[10px]">{count}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Filter size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    className="input-mil pl-7 text-xs"
                    placeholder="Filtrar por título/ID/responsável/tema"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="legado-search"
                  />
                </div>
                <button type="button" className="btn-ghost text-xs" onClick={reset} data-testid="legado-cancel">Limpar simulação</button>
                <button
                  type="button"
                  className="btn-gold text-xs flex items-center gap-1 disabled:opacity-50"
                  onClick={confirmImport}
                  disabled={confirming || selectedCount === 0}
                  data-testid="legado-confirm-btn"
                >
                  {confirming ? <><Loader2 size={12} className="animate-spin" /> Importando…</> : <><CheckCircle2 size={12} /> Confirmar importação ({selectedCount})</>}
                </button>
              </div>
            </div>
          </div>

          <div className="card-mil overflow-hidden">
            <table className="w-full text-sm" data-testid="legado-table">
              <thead>
                <tr>
                  <th className="px-3 py-2 border-b border-[var(--border)] w-8 text-center">
                    {activeTab === "valid" && (
                      <input
                        type="checkbox"
                        checked={visibleRows.length > 0 && visibleRows.every((r) => selected[r.row_index])}
                        onChange={(e) => toggleAllInTab(visibleRows, e.target.checked)}
                        data-testid="legado-check-all"
                      />
                    )}
                  </th>
                  {["#", "ID", "Resp.", "Ano", "Título", "Tipo", "Tema", "Idioma", "Status link", "DOI", "Observação"].map((h) => (
                    <th key={h} className="px-3 py-2 label-mil border-b border-[var(--border)] text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => {
                  const p = (r && r.parsed) || {};
                  const obsParts = [];
                  if (Array.isArray(r?.errors) && r.errors.length) obsParts.push(r.errors.join("; "));
                  if (r?.duplicate_reason) obsParts.push(r.duplicate_reason);
                  if (p.observacoes_legado) obsParts.push(`Obs: ${p.observacoes_legado}`);
                  return (
                    <tr key={stableKey(r)} className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)]">
                      <td className="px-3 py-2 text-center">
                        {activeTab === "valid" ? (
                          <input
                            type="checkbox"
                            checked={!!selected[r.row_index]}
                            onChange={() => toggleSelect(r.row_index)}
                            data-testid={`legado-check-${r.row_index}`}
                          />
                        ) : null}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-[var(--text-muted)]"><span>{r.row_index ?? "—"}</span></td>
                      <td className="px-3 py-2 font-mono text-xs"><span>{p.legacy_id ?? "—"}</span></td>
                      <td className="px-3 py-2 text-xs"><span>{p.responsavel_legado || "Não informado"}</span></td>
                      <td className="px-3 py-2 font-mono text-xs"><span>{p.ano_publicacao ?? "—"}</span></td>
                      <td className="px-3 py-2 text-xs max-w-md truncate" title={p.titulo || ""}>
                        {p.titulo ? <span>{p.titulo}</span> : <span className="text-[var(--danger)]">Sem título</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-[var(--text-muted)] max-w-[120px] truncate" title={p.tipo_fonte || ""}><span>{p.tipo_fonte || "—"}</span></td>
                      <td className="px-3 py-2 text-xs text-[var(--text-muted)] max-w-[160px] truncate" title={p.tema_legado || ""}><span>{p.tema_legado || "—"}</span></td>
                      <td className="px-3 py-2 text-xs"><span>{p.idioma || "—"}</span></td>
                      <td className="px-3 py-2"><StatusLinkBadge value={p.status_link} /></td>
                      <td className="px-3 py-2 font-mono text-[10px] max-w-[140px] truncate" title={p.doi || ""}><span>{p.doi || "Não informado"}</span></td>
                      <td className="px-3 py-2 text-[11px] text-[var(--text-muted)] max-w-[260px] truncate" title={obsParts.join(" · ")}>
                        <span>{obsParts.length ? obsParts.join(" · ") : "—"}</span>
                      </td>
                    </tr>
                  );
                })}
                {visibleRows.length === 0 && (
                  <tr key="empty-row"><td colSpan={12} className="text-center p-6 text-[var(--text-muted)] text-sm"><span>Nenhuma linha nesta aba.</span></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* IMPORT RESULT */}
      {imported && (
        <div className="card-mil p-5 gold-accent-top" data-testid="legado-imported-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={20} className="text-[var(--success)]" />
            <div className="font-display text-lg font-bold">Importação concluída</div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="label-mil text-[var(--text-muted)]">Inseridos</div>
              <div className="font-mono text-2xl text-[var(--success)]">{imported.inserted}</div>
            </div>
            <div>
              <div className="label-mil text-[var(--text-muted)]">Ignorados na confirmação</div>
              <div className="font-mono text-2xl text-[var(--warn)]">{imported.skipped}</div>
            </div>
          </div>
          {imported.skipped_items?.length > 0 && (
            <div className="mt-3">
              <div className="label-mil text-[var(--text-muted)] mb-1">Motivos dos ignorados</div>
              <ul className="text-xs font-mono text-[var(--text-muted)] list-disc list-inside max-h-40 overflow-auto">
                {imported.skipped_items.map((x) => (
                  <li key={x.row_index}>linha {x.row_index}: {x.reason}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-4 text-xs text-[var(--text-muted)]">
            Os registros agora aparecem na <span className="font-bold">Biblioteca</span> com a origem
            <span className="badge badge-info mx-1">Base Legada LSC</span> e podem ser enriquecidos com IA na Fase 2.
          </div>
        </div>
      )}

      {/* FASE 2 — Base já importada + enriquecimento IA */}
      <EnrichmentPanel onChanged={() => setStatsTick((t) => t + 1)} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-[var(--text-muted)]">{label}</span>
      <span className="font-mono tabular-nums">{value || 0}</span>
    </div>
  );
}
